<template>
  <div class="slider-wrapper">
    <div
      class="slider"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
      @keydown.left.prevent="prev"
      @keydown.right.prevent="next"
      tabindex="0"
      role="region"
      :aria-label="ariaLabel"
    >
      <!-- Left button -->
      <button
        class="nav-btn nav-left"
        @click="prev"
        :aria-label="'Previous slide'"
      >
        <Arrow color="black" style="transform: rotate(-90deg)" />
      </button>

      <!-- Track / viewport -->
      <div
        class="viewport"
        ref="viewportRef"
        @touchstart.passive="onTouchStart"
        @touchmove.prevent="onTouchMove"
        @touchend="onTouchEnd"
      >
        <div class="track" :style="trackStyle" role="list" aria-live="polite">
          <div
            v-for="(item, idx) in displayedItems"
            :key="itemKey(item, idx)"
            class="slide"
            :style="slideStyle"
          >
            <slot name="slide" :item="item" :index="idx">
              <img
                v-if="isStringOrUrl(item)"
                :src="String(item)"
                :alt="`Slide ${idx + 1}`"
              />
              <div v-else class="slide-default">
                {{ item }}
              </div>
            </slot>
          </div>
        </div>
      </div>

      <!-- Right button -->
      <button
        class="nav-btn nav-right"
        @click="next"
        :aria-label="'Next slide'"
      >
        <Arrow color="black" style="transform: rotate(90deg)" />
      </button>
    </div>

    <!-- Indicators -->
    <div v-if="showIndicators" class="indicators" aria-hidden="false">
      <button
        v-for="n in indicatorCount"
        :key="n - 1"
        class="indicator"
        :class="{ active: n - 1 === currentIndex }"
        @click="goTo(n - 1)"
        :aria-label="`Go to slide ${n}`"
        :aria-pressed="n - 1 === currentIndex"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, nextTick } from "vue"
import Arrow from "../icons/Arrow.vue"

type SlideItem = any

export default defineComponent({
  name: "SliderComponent",
  components: {
    Arrow,
  },
  props: {
    items: {
      type: Array as PropType<SlideItem[]>,
      required: true,
    },
    visible: {
      type: Number,
      default: 1,
      validator: (v: number) => v >= 1,
    },
    loop: {
      type: Boolean,
      default: true,
    },
    autoplay: {
      type: Number as PropType<number | null>,
      default: null,
    },
    pauseOnHover: {
      type: Boolean,
      default: true,
    },
    showIndicators: {
      type: Boolean,
      default: true,
    },
    ariaLabel: {
      type: String,
      default: "Image slider",
    },
    itemKeyProp: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      viewportRef: null as HTMLElement | null,
      currentIndex: 0,
      timerId: null as number | null,
      hovering: false,
      transitioning: false,
      swipeStartX: null as number | null,
      swipeDeltaX: 0,
      isDragging: false,
      touching: false,
    }
  },
  computed: {
    displayedItems(): SlideItem[] {
      // Clone last & first for seamless transition
      if (!this.loop || this.items.length === 0) return this.items
      const firstClone = this.items[0]
      const lastClone = this.items[this.items.length - 1]
      return [lastClone, ...this.items, firstClone]
    },
    maxIndex(): number {
      return Math.max(0, this.items.length - this.visible)
    },
    slideWidthPercent(): number {
      return 100 / this.visible / this.displayedItems.length
    },
    trackTranslatePercent(): number {
      // base translate includes the leading clone
      return (this.currentIndex + 1) * (100 / this.displayedItems.length)
    },

    viewportWidth(): number {
      return this.viewportRef?.clientWidth || 1
    },

    trackStyle(): Record<string, string> {
      const totalWidth = this.displayedItems.length * (100 / this.visible)
      const deltaPercent =
        this.isDragging && this.viewportRef
          ? (this.swipeDeltaX / this.viewportRef.clientWidth) * 100
          : 0

      const translate = this.trackTranslatePercent - deltaPercent

      return {
        transform: `translateX(-${translate}%)`,
        width: `${totalWidth}%`,
        transition:
          this.transitioning && !this.isDragging
            ? "transform 350ms ease"
            : "none",
        display: "flex",
      }
    },
    slideStyle(): Record<string, string> {
      return {
        flex: `0 0 ${this.slideWidthPercent}%`,
        boxSizing: "border-box",
      }
    },
    indicatorCount(): number {
      return Math.max(1, this.items.length - this.visible + 1)
    },
  },
  mounted() {
    this.startAutoplay()
  },
  beforeUnmount() {
    this.clearTimer()
  },
  methods: {
    onTouchStart(e: TouchEvent) {
      if (e.touches.length !== 1) return
      this.swipeStartX = e.touches[0].clientX
      this.swipeDeltaX = 0
      this.isDragging = false
      this.touching = true
      this.clearTimer() // pause autoplay on mobile
    },

    onTouchMove(e: TouchEvent) {
      if (this.swipeStartX === null) return
      const currentX = e.touches[0].clientX
      this.swipeDeltaX = currentX - this.swipeStartX

      // Only mark as dragging if moved enough
      if (!this.isDragging && Math.abs(this.swipeDeltaX) > 10) {
        this.isDragging = true
      }

      // Prevent vertical scroll only if dragging
      if (this.isDragging) e.preventDefault()
    },

    onTouchEnd() {
      if (!this.isDragging) {
        // no real swipe
        this.swipeStartX = null
        this.swipeDeltaX = 0
        this.touching = false
        this.startAutoplay()
        return
      }

      const threshold = 30 // minimum swipe distance to trigger a slide
      if (this.swipeDeltaX > threshold) {
        this.prev() // move one slide
      } else if (this.swipeDeltaX < -threshold) {
        this.next() // move one slide
      }

      // reset state
      this.swipeStartX = null
      this.swipeDeltaX = 0
      this.isDragging = false
      this.touching = false

      // resume autoplay
      this.startAutoplay()
    },

    itemKey(item: SlideItem, idx: number) {
      if (
        this.itemKeyProp &&
        typeof item === "object" &&
        item[this.itemKeyProp]
      )
        return item[this.itemKeyProp]
      return idx
    },
    isStringOrUrl(s: string | object): boolean {
      return typeof s === "string"
    },
    isSlideHidden() {
      return false // not strictly necessary now
    },
    prev() {
      if (this.items.length <= 1) return
      if (this.transitioning) return
      this.transitioning = true
      this.currentIndex--
      this.resetAutoplay()
      this.afterTransitionCheck()
    },
    next() {
      if (this.items.length <= 1) return
      if (this.transitioning) return
      this.transitioning = true
      this.currentIndex++
      this.resetAutoplay()
      this.afterTransitionCheck()
    },
    goTo(index: number) {
      if (this.transitioning || index === this.currentIndex) return

      this.transitioning = true
      this.currentIndex = index
      this.resetAutoplay()

      // ensure the transition runs normally
      setTimeout(() => {
        this.afterTransitionCheck()
      }, 350)
    },
    afterTransitionCheck() {
      // Wait for animation to end
      setTimeout(() => {
        if (!this.loop) {
          this.transitioning = false
          return
        }

        // Jump from clones to real slides instantly
        if (this.currentIndex >= this.items.length) {
          // after last clone -> go to first real
          this.transitioning = false
          this.currentIndex = 0
        } else if (this.currentIndex < 0) {
          // before first clone -> go to last real
          this.transitioning = false
          this.currentIndex = this.items.length - 1
        }

        nextTick(() => {
          // Disable transition, jump to real position instantly
          this.transitioning = false
        })
      }, 350)
    },
    startAutoplay() {
      if (!this.autoplay || this.autoplay <= 0) return
      this.clearTimer()
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.timerId = window.setInterval(() => {
        if (this.pauseOnHover && this.hovering) return
        this.next()
      }, this.autoplay)
    },
    clearTimer() {
      if (this.timerId != null) {
        window.clearInterval(this.timerId)
        this.timerId = null
      }
    },
    resetAutoplay() {
      this.clearTimer()
      this.startAutoplay()
    },
    onMouseEnter() {
      this.hovering = true
      if (this.pauseOnHover) this.clearTimer()
    },
    onMouseLeave() {
      this.hovering = false
      if (this.pauseOnHover) this.startAutoplay()
    },
  },
})
</script>

<style scoped>
.slider-wrapper {
  padding-bottom: 5px;
  background: #212429;
}
.slider {
  position: relative;
  display: flex;
  /* flex-direction: column; */
  align-items: center;
  gap: 8px;
  user-select: none;
  outline: none; /* keyboard focus styles handled below */
}

.viewport {
  overflow: hidden;
  width: 100%;
}

.track {
  will-change: transform;
  display: flex;
}

.slide {
  padding: 8px;
}

/* default slide content fallback */
.slide-default {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 180px;
  border-radius: 8px;
  background: #f2f2f2;
  color: #333;
  font-weight: 600;
}

/* simple responsive image fit */
.slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 6px;
  pointer-events: none; /* ✅ allow touch events to pass through */
  user-select: none; /* optional: prevent text selection on drag */
}

/* Nav buttons */
.nav-btn {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: inline-grid;
  place-items: center;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  transition: transform 120ms ease;
  position: absolute;
  top: calc(50% - 18px);
  z-index: 2;
}
.nav-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.nav-btn:not(:disabled):hover {
  transform: scale(1.05);
}

.nav-btn.nav-left {
  left: 0;
}

.nav-btn.nav-right {
  right: 0;
}
/* Indicators */
.indicators {
  display: flex;
  gap: 8px;
  margin-left: 8px;
  align-items: center;
  justify-content: center;
}
.indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.2);
  border: none;
  padding: 0;
  cursor: pointer;
}
.indicator.active {
  background: rgba(0, 0, 0, 0.7);
}

/* focus visible */
.slider:focus {
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.15);
  border-radius: 6px;
}

/* small screens tweak */
@media (max-width: 520px) {
  .slide-default {
    height: 140px;
  }
  .nav-btn {
    width: 32px;
    height: 32px;
    font-size: 18px;
  }
}
</style>
